import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

type FormatRelativeTimeResult = {
  value: number | string;
  unit: 'm' | 'h' | 'd' | 'mo' | 'date';
};

const splitSecondsIntoHMS = (decimalSeconds: number) => {
  const seconds = Math.round(decimalSeconds);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  return { hours, minutes, seconds: remainingSeconds };
};

export const secondsToTimeString = (decimalSeconds: number): string => {
  const { hours, minutes, seconds } = splitSecondsIntoHMS(decimalSeconds);

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
};

export const getTwoMonthsPeriodSinceDate = (date: string) => {
  const start = dayjs(date).subtract(2, 'month');
  return `${start.format('MMM YYYY')} - ${dayjs(date).format('MMM YYYY')}`;
};

export const formatToMonthYear = (date: string | Dayjs) => {
  return dayjs(date).format('MMM YYYY');
};

export const formatToShortMonthYear = (date: string | Dayjs) => {
  return dayjs(date).format('MMM YY');
};

export const formatRelativeTime = (datetime: string): FormatRelativeTimeResult => {
  const now = dayjs();
  const date = dayjs(datetime);
  const diffMinutes = now.diff(date, 'minute');

  if (diffMinutes < 60) {
    return { value: Math.max(diffMinutes, 1), unit: 'm' };
  }

  const diffHours = now.diff(date, 'hour');

  if (diffHours < 24) {
    return { value: diffHours, unit: 'h' };
  }

  const diffDays = now.diff(date, 'day');

  if (diffDays < 31) {
    return { value: diffDays, unit: 'd' };
  }

  const diffMonths = now.diff(date, 'month');

  if (diffMonths < 12) {
    return { value: diffMonths, unit: 'mo' };
  }

  return { value: date.format('MM/DD/YYYY HH:mm'), unit: 'date' };
};

export const secondsToHMS = (decimalSeconds: number): string => {
  const { hours, minutes, seconds } = splitSecondsIntoHMS(decimalSeconds);

  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }
  if (seconds > 0 || parts.length == 0) {
    parts.push(`${seconds}s`);
  }

  return parts.join(' ');
};
